import {
    formatComparisonChangeSummary,
    formatComparisonMetadataValue,
    getComparisonConfidenceScore,
    getComparisonMetadata,
    isComparisonRiskComment,
} from '../../../utils/comparisonRisk';

const cleanText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value);
    } catch (_) {
        return String(value);
    }
};

export const htmlToExportText = (value = '') => {
    const text = cleanText(value);
    if (!text) return '';

    try {
        const readableHtml = text
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/(p|div|h[1-6]|li|section|article)>/gi, '</$1>\n');
        const doc = new DOMParser().parseFromString(readableHtml, 'text/html');
        return (doc.body.textContent || '')
            .replace(/\u00a0/g, ' ')
            .replace(/[ \t]+\n/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    } catch (_) {
        return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }
};

const getRiskValue = (risk) => {
    if (!risk || typeof risk !== 'object') return cleanText(risk);
    return cleanText(risk.level || risk.severity || risk.label || risk.value || risk);
};

const getMoreExport = (comment, moreStateById, fastGlobalAnalysisById, labels) => {
    const fastGlobal = fastGlobalAnalysisById?.[comment?.id] || null;
    const local = moreStateById?.[comment?.id] || null;
    const state = fastGlobal || local || {};
    const content = state.content || comment?.more_content || '';
    const status = content
        ? labels.statuses.ready
        : state.status === 'running'
            ? labels.statuses.running
            : state.status === 'error'
                ? labels.statuses.error
                : labels.statuses.notGenerated;

    return {
        content: htmlToExportText(content),
        status,
    };
};

const formatRisk = (comment, labels) => {
    const value = getRiskValue(comment?.risk).toLowerCase();
    return labels.riskLevels[value] || getRiskValue(comment?.risk);
};

const buildRiskRow = (comment, states, labels) => {
    const quickView = comment?.quick_view || {};
    const more = getMoreExport(comment, states.moreStateById, states.fastGlobalAnalysisById, labels);

    return {
        criterion: cleanText(comment?.criterion_title),
        title: cleanText(comment?.title),
        risk: formatRisk(comment, labels),
        rationale: cleanText(
            comment?.risk_rationale || quickView?.risk_rationale || quickView?.riskRationale
        ),
        summary: cleanText(quickView?.description || comment?.description),
        action: cleanText(quickView?.action || comment?.suggested_action),
        reference: cleanText(comment?.doc_ref || comment?.ref),
        page: cleanText(comment?.page),
        quote: cleanText(comment?.quote),
        more: more.content,
        moreStatus: more.status,
    };
};

const buildComparisonRow = (comment, states, labels, t) => {
    const metadata = getComparisonMetadata(comment) || {};
    const quickView = comment?.quick_view || {};
    const more = getMoreExport(comment, states.moreStateById, states.fastGlobalAnalysisById, labels);
    const score = getComparisonConfidenceScore(metadata);

    return {
        changeType: formatComparisonMetadataValue(metadata.change_type, t) || labels.comparisonRisk,
        title: cleanText(comment?.title),
        baseSection: cleanText(metadata.base_section),
        baseText: cleanText(metadata.base_text),
        newSection: cleanText(metadata.new_section),
        newText: cleanText(metadata.new_text),
        summary: formatComparisonChangeSummary(metadata.change_summary, metadata.change_type, t),
        effect: cleanText(quickView?.risk || quickView?.description || comment?.description),
        risk: formatRisk(comment, labels),
        action: cleanText(quickView?.action || comment?.suggested_action),
        confidence: score === null ? '' : `${score} %`,
        more: more.content,
        moreStatus: more.status,
    };
};

export const buildCommentWorkbookLabels = (t) => ({
    sheets: {
        risks: t('sidebar.exportWorkbook.sheets.risks', { defaultValue: 'Risques' }),
        comparison: t('sidebar.exportWorkbook.sheets.comparison', { defaultValue: 'Comparaison' }),
    },
    statuses: {
        ready: t('sidebar.exportWorkbook.statuses.ready', { defaultValue: 'Disponible' }),
        running: t('sidebar.exportWorkbook.statuses.running', { defaultValue: 'En cours' }),
        error: t('sidebar.exportWorkbook.statuses.error', { defaultValue: 'Erreur' }),
        notGenerated: t('sidebar.exportWorkbook.statuses.notGenerated', { defaultValue: 'Non généré' }),
    },
    riskLevels: {
        critical: t('risk.critical', { defaultValue: 'Critique' }),
        high: t('risk.high', { defaultValue: 'Élevé' }),
        medium: t('risk.medium', { defaultValue: 'Moyen' }),
        low: t('risk.low', { defaultValue: 'Faible' }),
    },
    comparisonRisk: t('comparison.badge_risk', { defaultValue: 'Comparaison' }),
    columns: {
        criterion: t('sidebar.exportWorkbook.columns.criterion', { defaultValue: 'Critère' }),
        title: t('sidebar.exportWorkbook.columns.title', { defaultValue: 'Titre' }),
        risk: t('sidebar.exportWorkbook.columns.risk', { defaultValue: 'Niveau de risque' }),
        rationale: t('sidebar.exportWorkbook.columns.rationale', { defaultValue: 'Justification' }),
        summary: t('sidebar.exportWorkbook.columns.summary', { defaultValue: 'Résumé' }),
        action: t('sidebar.exportWorkbook.columns.action', { defaultValue: 'Action proposée' }),
        reference: t('sidebar.exportWorkbook.columns.reference', { defaultValue: 'Référence' }),
        page: t('sidebar.exportWorkbook.columns.page', { defaultValue: 'Page' }),
        quote: t('sidebar.exportWorkbook.columns.quote', { defaultValue: 'Extrait' }),
        more: t('sidebar.exportWorkbook.columns.more', { defaultValue: 'Analyse approfondie' }),
        moreStatus: t('sidebar.exportWorkbook.columns.moreStatus', { defaultValue: "Statut de l'approfondissement" }),
        changeType: t('sidebar.exportWorkbook.columns.changeType', { defaultValue: 'Type de changement' }),
        baseSection: t('sidebar.exportWorkbook.columns.baseSection', { defaultValue: 'Section de référence' }),
        baseText: t('sidebar.exportWorkbook.columns.baseText', { defaultValue: 'Extrait de référence' }),
        newSection: t('sidebar.exportWorkbook.columns.newSection', { defaultValue: 'Section du contrat reçu' }),
        newText: t('sidebar.exportWorkbook.columns.newText', { defaultValue: 'Extrait du contrat reçu' }),
        effect: t('sidebar.exportWorkbook.columns.effect', { defaultValue: 'Effet ou risque' }),
        confidence: t('sidebar.exportWorkbook.columns.confidence', { defaultValue: 'Confiance' }),
    },
});

export const buildCommentWorkbookData = ({
    primaryComments = [],
    comparisonChanges = [],
    uncertainComparisonChanges = [],
    includeUncertainComparisonChanges = true,
    moreStateById = {},
    fastGlobalAnalysisById = {},
    labels,
    t,
} = {}) => {
    const states = { moreStateById, fastGlobalAnalysisById };
    const riskComments = primaryComments.filter((comment) => !isComparisonRiskComment(comment));
    const comparisonComments = [
        ...primaryComments.filter(isComparisonRiskComment),
        ...comparisonChanges,
        ...(includeUncertainComparisonChanges ? uncertainComparisonChanges : []),
    ];

    return {
        riskRows: riskComments.map((comment) => buildRiskRow(comment, states, labels)),
        comparisonRows: comparisonComments.map((comment) => buildComparisonRow(comment, states, labels, t)),
    };
};

const RISK_COLUMNS = [
    ['criterion', 24], ['title', 34], ['risk', 16], ['rationale', 34], ['summary', 42],
    ['action', 38], ['reference', 18], ['page', 10], ['quote', 42], ['more', 52], ['moreStatus', 22],
];

const COMPARISON_COLUMNS = [
    ['changeType', 22], ['title', 34], ['baseSection', 24], ['baseText', 44], ['newSection', 24],
    ['newText', 44], ['summary', 38], ['effect', 38], ['risk', 16], ['action', 38],
    ['confidence', 14], ['more', 52], ['moreStatus', 22],
];

const buildSheetData = (rows, columns, labels) => [
    columns.map(([key]) => ({
        value: labels.columns[key],
        type: String,
        fontWeight: 'bold',
        color: '#FFFFFF',
        backgroundColor: '#17324D',
        alignVertical: 'center',
        wrap: true,
        height: 28,
    })),
    ...rows.map((row) => columns.map(([key]) => ({
        value: cleanText(row[key]),
        type: String,
        alignVertical: 'top',
        wrap: true,
        bottomBorderColor: '#D9E2EC',
        bottomBorderStyle: 'thin',
    }))),
];

export const buildCommentWorkbookSheets = ({ riskRows, comparisonRows, labels }) => ([
    {
        data: buildSheetData(riskRows, RISK_COLUMNS, labels),
        sheet: labels.sheets.risks,
        columns: RISK_COLUMNS.map(([, width]) => ({ width })),
        stickyRowsCount: 1,
        orientation: 'landscape',
    },
    {
        data: buildSheetData(comparisonRows, COMPARISON_COLUMNS, labels),
        sheet: labels.sheets.comparison,
        columns: COMPARISON_COLUMNS.map(([, width]) => ({ width })),
        stickyRowsCount: 1,
        orientation: 'landscape',
    },
]);

export const createCommentWorkbookBlob = async ({ riskRows, comparisonRows, labels }) => {
    const writerModule = await import('write-excel-file/browser');
    const writeExcelFile = writerModule.default || writerModule;
    return writeExcelFile(
        buildCommentWorkbookSheets({ riskRows, comparisonRows, labels })
    ).toBlob();
};

export const formatCommentWorkbookDate = (date = new Date()) => {
    const pad = (value) => String(value).padStart(2, '0');
    return [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())].join('-')
        + '-'
        + [pad(date.getHours()), pad(date.getMinutes()), pad(date.getSeconds())].join('-');
};

export const downloadCommentWorkbook = async ({ data, labels, filename }) => {
    const blob = await createCommentWorkbookBlob({ ...data, labels });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
